import { z } from 'zod';
import { isoDateTimeSchema } from './common.js';
/**
 * Health endpoints.
 *
 * Two distinct signals, because conflating them causes outages:
 *
 *  - **liveness** — is the process running? Never touches a dependency. If this
 *    checked the database, a brief Supabase blip would make Railway kill and
 *    restart every healthy container, turning a degradation into an outage.
 *  - **readiness** — can this instance serve traffic? Checks dependencies.
 */
export const dependencyStatusSchema = z.object({
    name: z.string(),
    status: z.enum(['up', 'down']),
    latencyMs: z.number().nonnegative().nullable(),
    detail: z.string().nullable(),
});
export const livenessSchema = z.object({
    status: z.literal('ok'),
    service: z.string(),
    version: z.string(),
    environment: z.string(),
    uptimeSeconds: z.number().nonnegative(),
    /**
     * When this image was built. The tripwire for a deploy that never
     * happened: CI compares it with the time of the push it is verifying.
     * Null outside a built image.
     */
    builtAt: isoDateTimeSchema.nullable(),
    timestamp: isoDateTimeSchema,
});
export const readinessSchema = z.object({
    status: z.enum(['ready', 'degraded']),
    dependencies: z.array(dependencyStatusSchema),
    timestamp: isoDateTimeSchema,
});
/**
 * The public status page: plain answers to "is it working", with nothing in
 * them about any business. Reading is "normal" while documents waiting to be
 * read are under fifteen minutes old, "slow" under an hour, "delayed" beyond.
 */
export const COMPONENT_STATES = ['operational', 'slow', 'delayed', 'down', 'off'];
export const componentStateSchema = z.enum(COMPONENT_STATES);
export const platformStatusSchema = z.object({
    overall: componentStateSchema,
    components: z.array(z.object({
        key: z.enum(['app', 'database', 'reading']),
        name: z.string(),
        state: componentStateSchema,
        detail: z.string().nullable(),
    })),
    checkedAt: isoDateTimeSchema,
});
//# sourceMappingURL=health.js.map